import {
  PageHeader,
  Button,
  Input,
  Sheet,
  SheetContent,
  SheetHeader,
  Toastify,
  SearchDropdownMultiSelect,
} from 'lift-ui';
import { RefreshCw, Plus, Search } from 'lucide-react';
import React, { useState, useEffect, useCallback, useMemo } from 'react';

import { AlertForm } from './components/AlertForm';
import { AlertFormStepHeader } from './components/AlertFormStepHeader';
import { AlertList } from './components/AlertList';
import { AlertSummaryStep } from './components/AlertSummaryStep';
import { useAlertFormData } from './hooks/useAlertFormData';
import { useAlerts } from './hooks/useAlerts';
import { Alert, AlertFilters, AlertFormData, Brand } from './types';
import { dmgAlertApiService } from './utils/dmgAlertApiService';
import { mapAlertFormDataToApi } from './utils/mappers';

const getStoredUser = (): { id?: number; email?: string; name?: string; team?: boolean } | null => {
  try {
    return JSON.parse(localStorage.getItem('user') as string);
  } catch {
    return null;
  }
};

export const DMGAlertSystem: React.FC = () => {
  const [filters, setFilters] = useState<AlertFilters>({});
  const [showAlertForm, setShowAlertForm] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [viewingAlert, setViewingAlert] = useState<Alert | null>(null);
  const [showViewAlert, setShowViewAlert] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [selectedAlertTypes, setSelectedAlertTypes] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [alertTypes, setAlertTypes] = useState<
    Array<{
      id: number;
      name: string;
      display_name: string;
      description: string;
      data_source: string;
      created_at: string;
      updated_at: string;
    }>
  >([]);
  const [alertFormCurrentStep, setAlertFormCurrentStep] = useState(1);
  const [alertFormCompletedSteps, setAlertFormCompletedSteps] = useState<number[]>([]);
  const alertFormRef = React.useRef<{ handleStepClick: (step: number) => void } | null>(null);

  const handleAlertFormStepChange = useCallback((step: number, completedSteps: number[]) => {
    setAlertFormCurrentStep(step);
    setAlertFormCompletedSteps(completedSteps);
  }, []);

  // Admin reads itself from localStorage (same pattern as the rest of lift-admin).
  const user = useMemo(() => getStoredUser(), []);

  // Admin always views ALL alerts (no user_id filter, no tabs).
  const allAlertsFilters = filters;

  const {
    alerts,
    loading,
    pagination,
    fetchAlerts,
    createAlert,
    updateAlert,
    deleteAlert,
    sendTestAlert,
  } = useAlerts(1, 15, 'global-alerts');

  const handleSheetInteractOutside = useCallback((event: Event) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest('.Toastify') || target?.closest('.Toastify__toast-container')) {
      event.preventDefault();
    }
  }, []);

  // Fetch brands and alert types for filters
  useEffect(() => {
    const loadFilterData = async () => {
      try {
        const brandsData = await dmgAlertApiService.getBrands();
        setBrands(brandsData as Brand[]);

        const alertTypesResponse = await dmgAlertApiService.getAlertTypes();
        setAlertTypes(alertTypesResponse.alert_types);
      } catch (error) {
        console.error('Error loading filter data:', error);
      }
    };

    loadFilterData();
  }, []);

  // Compute available filter options from current alerts
  const availableBrands = useMemo(() => {
    const uniqueBrandIds = new Set<string>();
    const brandsList: Array<{ id: string; label: string }> = [];

    alerts.forEach(alert => {
      if (alert.brands && Array.isArray(alert.brands)) {
        alert.brands.forEach(brand => {
          const brandId = brand.brand_id.toString();
          if (!uniqueBrandIds.has(brandId)) {
            uniqueBrandIds.add(brandId);
            brandsList.push({
              id: brandId,
              label: brand.brand_name,
            });
          }
        });
      }
    });

    return brandsList.sort((a, b) => a.label.localeCompare(b.label));
  }, [alerts]);

  const availableAlertTypes = useMemo(() => {
    const uniqueTypeIds = new Set<string>();
    const typesList: Array<{ id: string; label: string }> = [];

    alerts.forEach(alert => {
      if (alert.alert_type_id && alert.toolName) {
        const typeId = alert.alert_type_id.toString();
        if (!uniqueTypeIds.has(typeId)) {
          uniqueTypeIds.add(typeId);
          typesList.push({
            id: typeId,
            label: alert.toolName,
          });
        }
      }
    });

    return typesList.sort((a, b) => a.label.localeCompare(b.label));
  }, [alerts]);

  const getFiltersFromSelectedStates = (): AlertFilters => {
    const newFilters: AlertFilters = {};

    if (selectedBrands.length > 0) {
      newFilters.brand_id = parseInt(selectedBrands[0]);
    }
    if (selectedAlertTypes.length > 0) {
      newFilters.alert_type_id = parseInt(selectedAlertTypes[0]);
    }
    if (selectedStatuses.length === 1) {
      newFilters.is_active = selectedStatuses[0] === 'active';
    }

    return newFilters;
  };

  // Initial load
  useEffect(() => {
    fetchAlerts(1, pagination.pageSize, allAlertsFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (showAlertForm && !selectedAlert) {
      setAlertFormCurrentStep(1);
      setAlertFormCompletedSteps([]);
    }
  }, [showAlertForm, selectedAlert]);

  const handlePageChange = (page: number) => {
    fetchAlerts(page, pagination.pageSize, allAlertsFilters);
  };

  const handlePageSizeChange = (size: number) => {
    fetchAlerts(1, size, allAlertsFilters);
  };

  const handleRefresh = () => {
    fetchAlerts(pagination.currentPage, pagination.pageSize, allAlertsFilters);
  };

  const handleAddNewAlert = () => {
    setSelectedAlert(null);
    setAlertFormCurrentStep(1);
    setAlertFormCompletedSteps([]);
    setShowAlertForm(true);
  };

  const isTestLabAlert = (alert?: Alert | null) => alert?.toolName === 'Test Lab Alert';

  const handleEditAlert = (alert: Alert) => {
    if (isTestLabAlert(alert)) {
      Toastify('error', 'Test Lab alerts cannot be edited');
      return;
    }
    setIsDuplicating(false);
    setSelectedAlert(alert);
    setShowAlertForm(true);
  };

  const clearAlertTemplateStorage = (userId?: number) => {
    if (!userId) return;
    try {
      const prefix = `dmg-alert-templates-${userId}-`;
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.error('Error clearing alert template storage:', error);
    }
  };

  const handleAlertFormClose = () => {
    clearAlertTemplateStorage(user?.id);
    setShowAlertForm(false);
    setSelectedAlert(null);
    setIsDuplicating(false);
    setAlertFormCurrentStep(1);
    setAlertFormCompletedSteps([]);
  };

  const handleAlertFormSubmit = async (alertData: AlertFormData, selectedBrandsArg: any[]) => {
    try {
      if (selectedAlert && !isDuplicating) {
        if (isTestLabAlert(selectedAlert)) {
          Toastify('error', 'Test Lab alerts cannot be updated');
          return;
        }
        // Admin edits the alert on behalf of its original owner.
        const ownerUserId = selectedAlert.ownerUserId || user?.id || 0;
        const apiData = mapAlertFormDataToApi(
          alertData,
          ownerUserId,
          selectedBrandsArg,
          alertData.alertTypeId,
          selectedAlert.ownerEmail || user?.email,
          selectedAlert,
          undefined
        );

        const alertId = selectedAlert.id ? parseInt(selectedAlert.id, 10) : undefined;
        if (!alertId) {
          Toastify('error', 'Alert ID is missing');
          return;
        }

        await updateAlert(alertId, ownerUserId, apiData);
        Toastify('success', 'Alert updated successfully');
        await fetchAlerts(pagination.currentPage, pagination.pageSize, allAlertsFilters);
      } else {
        const apiData = mapAlertFormDataToApi(
          alertData,
          user?.id || 0,
          selectedBrandsArg,
          alertData.alertTypeId,
          user?.email,
          undefined,
          undefined
        );
        await createAlert(apiData);
        await fetchAlerts(1, pagination.pageSize, allAlertsFilters);
        Toastify('success', 'Alert created successfully');
      }
      handleAlertFormClose();
    } catch (error) {
      console.error('Error saving alert:', error);
      Toastify('error', 'Failed to save alert');
    }
  };

  const handleDeleteAlert = async (alertId: number, userId: number) => {
    const targetAlert = alerts.find(a => a.id != null && parseInt(a.id, 10) === alertId);
    if (isTestLabAlert(targetAlert)) {
      Toastify('error', 'Test Lab alerts cannot be deleted');
      return;
    }
    try {
      await deleteAlert(alertId, userId);
    } catch (error) {
      console.error('Error deleting alert:', error);
    }
  };

  const handleSendTestAlert = async (alertId: string) => {
    try {
      const success = await sendTestAlert(alertId);
      if (success) {
        Toastify('success', 'Test alert sent successfully');
      } else {
        Toastify('error', 'Failed to send test alert');
      }
    } catch (error) {
      console.error('Error sending test alert:', error);
      Toastify('error', 'Error sending test alert');
    }
  };

  const handleFiltersChange = (newFilters: AlertFilters) => {
    setFilters(newFilters);
    fetchAlerts(1, pagination.pageSize, newFilters);
  };

  useEffect(() => {
    const newFilters = getFiltersFromSelectedStates();
    handleFiltersChange(newFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrands, selectedAlertTypes, selectedStatuses]);

  const handleClearFilters = () => {
    setSelectedBrands([]);
    setSelectedAlertTypes([]);
    setSelectedStatuses([]);
  };

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        tabs={[]}
        title="Alerting System"
        renderCustomTop={
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center gap-2 h-8 px-3"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={handleAddNewAlert}
              size="sm"
              className="flex items-center gap-2 h-8 px-3"
            >
              <Plus className="h-4 w-4" />
              Add New Alert
            </Button>
          </div>
        }
      />

      <div className="flex-1 px-8 pt-8 pb-4 overflow-hidden">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="w-[200px]">
              <SearchDropdownMultiSelect
                items={availableBrands}
                selectedItems={selectedBrands}
                onSelectionChange={setSelectedBrands}
                placeholder="Search brands"
                triggerLabel="Filter by Brand"
              />
            </div>
            <div className="w-[200px]">
              <SearchDropdownMultiSelect
                items={availableAlertTypes}
                selectedItems={selectedAlertTypes}
                onSelectionChange={setSelectedAlertTypes}
                placeholder="Search alert types"
                triggerLabel="Filter by Alert Type"
              />
            </div>
            {(selectedBrands.length > 0 ||
              selectedAlertTypes.length > 0 ||
              selectedStatuses.length > 0) && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearFilters}
                className="flex items-center gap-2"
              >
                Clear Filters
              </Button>
            )}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
            <Input
              placeholder="Search alerts..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              className="w-64 pl-10"
            />
          </div>
        </div>

        <AlertList
          alerts={
            searchText.trim()
              ? alerts.filter(a => {
                  const q = searchText.toLowerCase();
                  return (
                    (a.name || '').toLowerCase().includes(q) ||
                    (a.description || '').toLowerCase().includes(q) ||
                    (a.toolName || '').toLowerCase().includes(q) ||
                    (a.brandName || '').toLowerCase().includes(q) ||
                    (a.alertMessage || '').toLowerCase().includes(q)
                  );
                })
              : alerts
          }
          loading={loading}
          pagination={pagination}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onEditAlert={handleEditAlert}
          onDeleteAlert={handleDeleteAlert}
          onSendTestAlert={handleSendTestAlert}
          onFiltersChange={handleFiltersChange}
          filters={allAlertsFilters}
          alertType="admin"
          currentUserId={user?.id}
          onViewAlert={alert => {
            setViewingAlert(alert);
            setShowViewAlert(true);
          }}
        />
      </div>

      <Sheet
        open={showAlertForm}
        onOpenChange={open => {
          if (!open) {
            clearAlertTemplateStorage(user?.id);
          }
          setShowAlertForm(open);
        }}
      >
        <SheetContent
          onInteractOutside={handleSheetInteractOutside}
          className="flex flex-col bg-[#FBFBFA] h-full min-h-0"
          style={{
            width: 'auto',
            minWidth: 'min(80vw, 600px)',
            maxWidth: 'min(95vw, 1600px)',
          }}
        >
          <SheetHeader className="flex flex-row items-center justify-between pr-8">
            <AlertFormStepHeader
              currentStep={alertFormCurrentStep}
              completedSteps={alertFormCompletedSteps}
              isEditMode={!!selectedAlert && !isDuplicating}
              isDuplicateMode={isDuplicating}
              onStepClick={step => {
                if (alertFormRef.current?.handleStepClick) {
                  alertFormRef.current.handleStepClick(step);
                }
              }}
            />
          </SheetHeader>
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            <AlertForm
              ref={alertFormRef}
              alert={selectedAlert}
              isDuplicating={isDuplicating}
              onClose={handleAlertFormClose}
              onSubmit={handleAlertFormSubmit}
              userEmail={selectedAlert?.ownerEmail || user?.email}
              userIsTeam={user?.team}
              userId={selectedAlert?.ownerUserId || user?.id}
              onStepChange={handleAlertFormStepChange}
            />
          </div>
        </SheetContent>
      </Sheet>

      {/* View Alert Summary Sheet - Read Only */}
      <Sheet open={showViewAlert} onOpenChange={setShowViewAlert}>
        <SheetContent
          onInteractOutside={handleSheetInteractOutside}
          className="flex flex-col bg-[#FBFBFA] h-full min-h-0"
          style={{
            width: 'auto',
            minWidth: 'min(80vw, 600px)',
            maxWidth: 'min(95vw, 1600px)',
          }}
        >
          <SheetHeader className="flex flex-row items-center justify-between pr-8 mb-4 flex-shrink-0">
            <h2 className="text-xl font-semibold">Alert Summary</h2>
          </SheetHeader>
          <div className="flex-1 min-h-0 overflow-y-auto">
            {viewingAlert && (
              <ViewSummaryErrorBoundary>
              <ViewAlertSummary
                alert={viewingAlert}
                userEmail={viewingAlert.ownerEmail || user?.email}
                userIsTeam={user?.team}
                userId={viewingAlert.ownerUserId || user?.id}
                alertTypes={alertTypes}
              />
              </ViewSummaryErrorBoundary>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

class ViewSummaryErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: unknown) {
    console.error('ViewAlertSummary render error:', error);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6 text-sm text-gray-600">
          Couldn&rsquo;t render the alert summary. Try editing the alert to see its details.
        </div>
      );
    }
    return this.props.children;
  }
}

const ViewAlertSummary: React.FC<{
  alert: Alert;
  userEmail?: string;
  userIsTeam?: boolean;
  userId?: number;
  alertTypes?: Array<{
    id: number;
    name: string;
    display_name: string;
    description: string;
    data_source: string;
    created_at: string;
    updated_at: string;
  }>;
}> = ({ alert, userEmail, userIsTeam, userId, alertTypes }) => {
  const { formData, selectedBrands } = useAlertFormData({
    alert,
    userEmail,
    userIsTeam,
  });

  const selectedCommunicationMethods = useMemo(
    () => formData.communicationMethods.filter(method => method.selected).map(method => method.id),
    [formData.communicationMethods]
  );

  return (
    <div className="w-full">
      <AlertSummaryStep
        formData={formData}
        selectedBrands={selectedBrands}
        selectedCommunicationMethods={selectedCommunicationMethods}
        onScheduleChange={() => {}}
        onMessageTemplateChange={() => {}}
        onRemoveThresholdRow={undefined}
        readOnly={true}
        userId={userId}
        alertTypes={alertTypes}
      />
    </div>
  );
};

export default DMGAlertSystem;
